// Picks a gradient + icon + label for the dashboard greeting based on
// time-of-day and (optionally) current weather.
//
// Open-Meteo weather codes (WMO):
//   0      Clear sky
//   1,2    Mainly clear, partly cloudy
//   3      Overcast
//   45,48  Fog
//   51-67  Drizzle / rain
//   71-77  Snow
//   80-82  Rain showers
//   85,86  Snow showers
//   95-99  Thunderstorm

const bucket = (code, cloudCover) => {
  if (code == null) return 'clear';
  // Precipitation / extreme weather always wins
  if (code === 45 || code === 48) return 'fog';
  if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82)) return 'rain';
  if ((code >= 71 && code <= 77) || code === 85 || code === 86) return 'snow';
  if (code >= 95) return 'storm';
  // Sky-condition codes — refine with cloud_cover when available so
  // "mainly clear" (code 1) with 5% clouds doesn't read as partly cloudy.
  if (typeof cloudCover === 'number') {
    if (cloudCover < 30) return 'clear';
    if (cloudCover < 70) return 'partly';
    return 'cloudy';
  }
  // Fallback when cloud_cover isn't available
  if (code === 0) return 'clear';
  if (code === 1) return 'clear';
  if (code === 2) return 'partly';
  if (code === 3) return 'cloudy';
  return 'clear';
};

const periodOf = (hour) => {
  if (hour < 5) return 'night';
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  if (hour < 21) return 'evening';
  return 'night';
};

const greetingFor = (period) => {
  if (period === 'morning') return 'Good morning';
  if (period === 'afternoon') return 'Good afternoon';
  if (period === 'evening') return 'Good evening';
  return 'Good evening'; // night also says "good evening"
};

// Gradient palette keyed by [period][weather].
// Each entry is two colors that get blended into a left-to-right gradient.
const palette = {
  morning: {
    clear:  ['#fbbf24', '#f97316'], // warm sunrise yellow → orange
    partly: ['#fcd34d', '#fb923c'],
    cloudy: ['#94a3b8', '#64748b'],
    fog:    ['#cbd5e1', '#94a3b8'],
    rain:   ['#60a5fa', '#0ea5e9'],
    snow:   ['#bfdbfe', '#93c5fd'],
    storm:  ['#6366f1', '#4f46e5'],
  },
  afternoon: {
    clear:  ['#facc15', '#3b82f6'], // golden → sky blue
    partly: ['#fcd34d', '#60a5fa'],
    cloudy: ['#94a3b8', '#64748b'],
    fog:    ['#cbd5e1', '#94a3b8'],
    rain:   ['#3b82f6', '#06b6d4'],
    snow:   ['#dbeafe', '#93c5fd'],
    storm:  ['#7c3aed', '#4338ca'],
  },
  evening: {
    clear:  ['#f97316', '#ec4899'], // sunset orange → pink
    partly: ['#fb923c', '#c084fc'],
    cloudy: ['#a78bfa', '#6366f1'],
    fog:    ['#94a3b8', '#7c3aed'],
    rain:   ['#3b82f6', '#7c3aed'],
    snow:   ['#fbcfe8', '#a5b4fc'],
    storm:  ['#7c3aed', '#1e1b4b'],
  },
  night: {
    clear:  ['#3b82f6', '#7c3aed'], // deep blue → purple
    partly: ['#1e40af', '#6366f1'],
    cloudy: ['#475569', '#334155'],
    fog:    ['#64748b', '#334155'],
    rain:   ['#1e40af', '#0e7490'],
    snow:   ['#cbd5e1', '#64748b'],
    storm:  ['#312e81', '#0f172a'],
  },
};

export function getGreetingTheme({ hour, weatherCode, cloudCover }) {
  const period = periodOf(hour);
  const weather = bucket(weatherCode, cloudCover);
  const colors = (palette[period] && palette[period][weather]) || palette[period].clear;
  return {
    period,
    weather,
    greeting: greetingFor(period),
    gradient: `linear-gradient(135deg, ${colors[0]} 0%, ${colors[1]} 100%)`,
    colors,
  };
}
